import { warn } from '../logger';
/** Lag liste med alle shadow roots, også nøstede shadow roots inne i andre webkomponenter */
const getShadowRoots = (scope = document) => Array.from(scope.querySelectorAll('*')).flatMap(element => element.shadowRoot ? [element.shadowRoot, ...getShadowRoots(element.shadowRoot)] : []);
const isStylesheetLink = (node) => node instanceof HTMLLinkElement && node.rel === 'stylesheet';
const normalizeTagNames = (tagName) => (Array.isArray(tagName) ? tagName : [tagName]).map(name => name.toLowerCase());
/** Finn alle link-elementer med rel="stylesheet" i en nodeliste, filtrert på href. */
const getHrefFilteredLinkElements = (nodes, hrefFilter) => {
    return Array.from(nodes)
        .filter(isStylesheetLink)
        .filter(link => (hrefFilter ? hrefFilter(link.getAttribute('href')) : true));
};
const getComponentShadowRoots = (tagName) => {
    const allRoots = getShadowRoots();
    const tagNames = new Set(normalizeTagNames(tagName));
    return allRoots.filter(root => tagNames.has(root.host.tagName.toLowerCase()));
};
const linkAlreadyInRoot = (root, href) => {
    if (!href) {
        return false;
    }
    const existing = root.querySelectorAll('link[rel="stylesheet"]');
    return Array.from(existing).some(link => link.href === href);
};
const addLinkToTemplate = (template, linkElement) => {
    const href = linkElement.href;
    if (linkAlreadyInRoot(template.content, href)) {
        return;
    }
    const cloned = linkElement.cloneNode(true);
    template.content.appendChild(cloned);
};
const addLinksToTemplate = (template, linkElements) => {
    linkElements.forEach(link => addLinkToTemplate(template, link));
};
const addLinksToComponentShadowRoots = (tagName, linkElements) => {
    const roots = getComponentShadowRoots(tagName);
    linkElements.forEach(linkElement => {
        const href = linkElement.href;
        roots.forEach(root => {
            if (linkAlreadyInRoot(root, href)) {
                return;
            }
            const cloned = linkElement.cloneNode(true);
            root.appendChild(cloned);
        });
    });
};
/** Fjerner et link-element fra head når det er lastet inn. */
const removeFromHeadWhenLoaded = (link) => {
    if (link.sheet) {
        link.remove();
        return;
    }
    const handleRemove = () => link.remove();
    link.addEventListener('load', handleRemove, { once: true });
    link.addEventListener('error', handleRemove, { once: true });
};
/**
 * Start observer som:
 *  - ser etter nye link-elementer med rel="stylesheet" i head (filtered by href)
 *  - legger dem i en template (templateId)
 *  - og inn i shadowRoot på alle komponenter med gitt(e) tagName(s).
 *
 * Merk: Ingen initial pass - kun nye link-tags blir håndtert.
 *
 * Stylesheetene fjernes fra header etter at de er lastet inn, for å unngå at de overstyrer CSS i vertikalen.
 *
 * Nyttig for webkomponenter som lazy-loades, da det gir dynamisk lasting av stylesheets.
 */
export const startStyleObserverForComponent = (options) => {
    const { templateId, tagName, hrefFilter } = options;
    const template = document.getElementById(templateId);
    if (!template) {
        warn(`startStyleObserverForComponent: Fant ikke <template> med id="${templateId}".`);
        return;
    }
    const processLinkElements = (linkElements) => {
        if (!linkElements.length) {
            return;
        }
        addLinksToTemplate(template, linkElements);
        addLinksToComponentShadowRoots(tagName, linkElements);
        linkElements.forEach(removeFromHeadWhenLoaded);
    };
    const observer = new MutationObserver(mutationList => {
        for (const mutation of mutationList) {
            if (mutation.type !== 'childList') {
                continue;
            }
            const addedLinks = getHrefFilteredLinkElements(mutation.addedNodes, hrefFilter);
            processLinkElements(addedLinks);
        }
    });
    observer.observe(document.head, {
        attributes: false,
        childList: true,
        subtree: false,
    });
};
